import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { JobDetailService } from '../../features/details/services/job-details.service';
import { CookieService } from '../services/cookie/cookie.service';
import { Cookies } from '../../shared/enums/app.enums';
import { toSignal } from '@angular/core/rxjs-interop';
import { ApplyOnlineService } from '../../features/apply/services/apply-online.service';
import { JobData, UserCvMatchingData, UserData, ApplyOnlineResponse } from '../../features/apply/interfaces/apply.interface';
import { APIResponse } from '../../features/details/models/details.model';
import { JOB_NOT_AVAILABLE_ERROR_MESSAGE, UPDATE_CV_ERROR_MESSAGE } from '../../shared/utils/app.const';
import { CircularLoaderService } from '../services/circularLoader/circular-loader.service';
import { C2C_ADTYPE } from '../../shared/consts/shared.const';
import { ToasterService } from '../../shared/services/toaster.service';
import { isPlatformServer, DOCUMENT } from '@angular/common';
import { RESPONSE_INIT } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { LangOptions } from '../../shared/models/models';
import { constructRedirectURL } from '../../features/jobs/utils/functions';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApplyOnlineGuards implements CanActivate {
  private readonly router = inject(Router);
  private readonly applyOnlineService = inject(ApplyOnlineService);
  private readonly jobDetailsService = inject(JobDetailService);
  private readonly cookieService = inject(CookieService);
  private readonly circularLoaderService = inject(CircularLoaderService);
  private platformId = inject(PLATFORM_ID)
  private document = inject(DOCUMENT);
  private response = inject(RESPONSE_INIT);
  private toaster = inject(ToasterService)
  private transloco = inject(TranslocoService);

  activeLang = toSignal(this.transloco.langChanges$ as Observable<LangOptions>,
    { initialValue: "en" }
  )

  private isProUser = toSignal(
    this.jobDetailsService.getIsProUser(
      decodeURIComponent(
        this.cookieService.getCookie(Cookies.MYBDJOBSGID) ?? ''
      )
    ),
    { initialValue: false }
  );

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const jobId: string = route.params['id'];
    // Reject invalid job IDs (e.g. chunk files, polyfills, or other non-numeric values)
    // if (!jobId || !/^\d+$/.test(jobId)) {
    //   void this.router.navigate(['/']);
    //   return of(false);
    // }

    const cvStatus: string = route.queryParams['cvStatus'];
    const rawUserId = this.cookieService.getCookie(Cookies.MyBDJOBSUSERID);
    const userId = decodeURIComponent(rawUserId ?? '');
    if (userId === null || userId === '' || userId === undefined) {
      const applyRedirectURL = constructRedirectURL('jd', `apply/${jobId}`, this.activeLang());
      if (isPlatformServer(this.platformId)) {
        const meta = this.document.createElement('meta');
        meta.httpEquiv = 'refresh';
        meta.content = `0;url=${applyRedirectURL}`;
        this.document.head.appendChild(meta);

        const script = this.document.createElement('script');
        script.text = `window.location.href = "${applyRedirectURL}";`;
        this.document.head.appendChild(script);

        // Safe-mock the API call for the current request scope to avoid SSR component crash
        this.applyOnlineService.getJobDataByUser = (): Observable<APIResponse<ApplyOnlineResponse>> => of({
          data: {
            JobData: null,
            UserData: null,
            BoostData: null,
            UserCvMatchingData: null,
            Miscellaneous: null
          }
        } as unknown as APIResponse<ApplyOnlineResponse>);

        return of(true);
      }
      else {
        window.location.href = applyRedirectURL as string;
        return of(false);
      }
    }
    // If cvStatus query param exists, allow access (used for specific scenarios)
    if (cvStatus) {
      return of(true);
    }
    
    return this.applyOnlineService.getJobDataByUser(jobId, userId).pipe(
      tap(() => this.circularLoaderService.setLoading(false)),
      map((response) => {
        const data = response.data;
        // // check user application limit
        // if (
        //   data.UserData.Limit !== -1 &&
        //   data.UserData.Used >= data.UserData.Limit
        // ) {
        //   // User has reached their application limit e.g., free or pro limit
        //   return this.checkUserLimitAndNavigate(jobId);
        // }

        if (data.Miscellaneous.Message === JOB_NOT_AVAILABLE_ERROR_MESSAGE) {
          return this.redirectSafe(['apply-online/job-not-available'], { jobId });
        }

        // Check profile completeness
        if (!this.checkUserProfileCompleteness(data.UserCvMatchingData, data.UserData)) {
          // User profile is incomplete
          return this.redirectSafe(['apply-online/profile-incomplete'], { jobId });
        }

        // Check user eligibility by the jobs requirements, navigate to the not eligible page
        if (data.UserData !== null && data.JobData !== null) {
          let isEligible: boolean | UrlTree = true;
          if (data.Miscellaneous.AdType !== C2C_ADTYPE) {
            isEligible = this.checkUserEligibility(data.JobData, data.UserData, jobId);
          }
          if (isEligible !== true) {
            return isEligible;
          }
        }
        // Check if user profile is updated, navigate to update cv page
        if (data.Miscellaneous.Message === UPDATE_CV_ERROR_MESSAGE) {
          return this.redirectSafe(['apply-online/update-cv'], { jobId });
        }

        if (
          data.UserData.Limit !== -1 &&
          data.UserData.Used >= data.UserData.Limit
        ) {
          // User has reached their application limit e.g., free or pro limit
          return this.checkUserLimitAndNavigate(jobId);
        }

        return true;
      }),
      catchError((err) => {
        console.log(err)
        this.circularLoaderService.setLoading(false)
        this.toaster.open("apiErrorMsg", "error");
        return of(false)
      })
    );
  }

  private redirectSafe(urlArray: string[], queryParams: Record<string, string | number | boolean>): UrlTree | boolean {
    const baseHref = environment.baseHref || '';
    const urlTree = this.router.createUrlTree(urlArray, { queryParams });
    if (isPlatformServer(this.platformId)) {
      // Remove trailing slash from baseHref and leading slash from serializedUrl to prevent a double slash
      const cleanBaseHref = baseHref.endsWith('/') ? baseHref.slice(0, -1) : baseHref;
      const serializedUrl = this.router.serializeUrl(urlTree);
      const urlPath = cleanBaseHref + (serializedUrl.startsWith('/') ? serializedUrl : '/' + serializedUrl);
      const meta = this.document.createElement('meta');
      meta.httpEquiv = 'refresh';
      meta.content = `0;url=${urlPath}`;
      this.document.head.appendChild(meta);

      const script = this.document.createElement('script');
      script.text = `window.location.href = "${urlPath}";`;
      this.document.head.appendChild(script);

      return true;
    }
    return urlTree;
  }

  private checkUserEligibility(jobData: JobData, userData: UserData, jobId: string): boolean | UrlTree {
    // Check Age
    if (
      jobData.DidCompanyRestrictedAge &&
      ((jobData.RequiredMinimumAge &&
        jobData.RequiredMinimumAge >= 0 &&
        userData.Age &&
        Math.round(parseFloat(userData.Age)) < jobData.RequiredMinimumAge) ||
        (jobData.RequiredMaximumAge &&
          jobData.RequiredMaximumAge >= 0 &&
          userData.Age &&
          Math.round(parseFloat(userData.Age)) > jobData.RequiredMaximumAge))
    ) {
      return this.redirectSafe(['apply-online/not-elligible'], { jobId });
    }
    // Check Experience
    if (
      jobData.DidCompanyRestrictedExperience &&
      ((jobData.RequiredMinimumExperience &&
        jobData.RequiredMinimumExperience >= 0 &&
        userData.Experience &&
        Math.round(parseFloat(userData.Experience)) < jobData.RequiredMinimumExperience) 
    //     ||
    //   (jobData.RequiredMaximumExperience &&
    //     jobData.RequiredMaximumExperience >= 0 &&
    //     userData.Experience &&
    //     Math.round(parseFloat(userData.Experience)) > jobData.RequiredMaximumExperience)
    // )
  )) {
      return this.redirectSafe(['apply-online/not-elligible'], { jobId });
    }
    // Check Gender
    if (
      jobData.RequiredGender &&
      userData.Gender &&
      !jobData.RequiredGender.includes(userData.Gender)
    ) {
      return this.redirectSafe(['apply-online/not-elligible'], { jobId });
    }
    return true;
  }

  private checkUserProfileCompleteness(data: UserCvMatchingData, user: UserData): boolean {
    if (
      (data.DoesUserHaveAcademicQualification === false && user.IsUserBlueCollar === false) ||
      data.DoesUserHavePersonalDetails === false ||
      data.DoesUserHavePhoto === false
    ) {
      return false;
    }
    return true;
  }

  private checkUserLimitAndNavigate(jobId:string): boolean | UrlTree {
    if (this.isProUser()) {
      return this.redirectSafe(['apply-online/pro-limit-reached'], { jobId });
    } else {
      return this.redirectSafe(['apply-online/free-limit-reached'], { jobId });
    }
  }
}
